/**
 * The org this order belongs to, as the meta line needs it. Structural, so each
 * app hands over its generated GraphQL object unchanged.
 */
export interface ClientMetaInput {
	countryCode?: string | null;
	createdAt?: string | null;
	addresses?:
		| {
				city: string;
				countryCode: string;
				isPrimary: boolean;
		  }[]
		| null;
}

/**
 * "Organization · Stavanger, NO · client since 2024".
 *
 * Every part is optional and drops out cleanly, because all of it is: an org can
 * have no addresses, no country, and — on old records — no createdAt. A client
 * with none of it still gets "Organization" rather than a line of stray
 * separators.
 *
 * The city comes from the primary address, falling back to the first one on
 * file. Not from the order's `deliveryAddress`: that is where these goods ship,
 * which is frequently a hotel, a warehouse or an event venue, and is not where
 * the client is.
 *
 * The year is taken from the ISO string rather than parsed into a Date. This
 * package formats no dates by design — the apps own locale — and "since 2024" is
 * a year, not a date. Slicing it keeps that true and avoids a timezone shifting
 * a January signup into the previous year.
 */
export function clientMeta(org: ClientMetaInput | null | undefined): string {
	if (!org) return 'Organization';

	const parts = ['Organization'];

	const address = org.addresses?.find((a) => a.isPrimary) ?? org.addresses?.[0];
	const city = address?.city?.trim();
	const country = (address?.countryCode ?? org.countryCode)?.trim();
	if (city && country) parts.push(`${city}, ${country}`);
	else if (city) parts.push(city);
	else if (country) parts.push(country);

	const year = org.createdAt?.slice(0, 4);
	if (year && /^\d{4}$/.test(year)) parts.push(`client since ${year}`);

	return parts.join(' · ');
}
